import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { StackGrid } from "@/components/calv/StackGrid";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/en/services/website-development")({
  head: () => {
    const h = pageHead("websiteDevelopment", "en");
    return { meta: h.meta, links: h.links };
  },
  component: WebsitePage,
});

const steps = [
  { num: "01", title: "Conception & Structure", body: "We develop page architecture, content logic, and user guidance based on clear goals and real user needs." },
  { num: "02", title: "Technical Implementation", body: "Implementation is clean, performant, and maintainable — with focus on load times, security, and extensibility." },
  { num: "03", title: "Integration & Preparation", body: "SEO fundamentals, tracking, performance marketing requirements, and interfaces are considered from the start." },
  { num: "04", title: "Further Development & Maintenance", body: "Even after go-live, we support our clients with optimization, expansion, and technical maintenance." },
];

const capabilities = [
  { name: "Page architecture", detail: "Content logic, user guidance" },
  { name: "Performance optimization", detail: "Load times, technical foundation" },
  { name: "SEO foundations", detail: "Integrated from the start" },
  { name: "Tracking integration", detail: "Structural, not retrofitted" },
  { name: "Marketing integration", detail: "Conversion-oriented" },
  { name: "Multilingual support", detail: "TYPO3, international setups" },
  { name: "Headless with Sanity", detail: "structured content layer" },
  { name: "Maintenance & evolution", detail: "Long-term, partnership-based" },
];

const stack = [
  { name: "WordPress", small: "flexible, editorial" },
  { name: "TYPO3", small: "Enterprise, multilingual" },
  { name: "Sanity", small: "Headless, cross-channel" },
];

function WebsitePage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Services · Build"
          headline={<>Websites that <em>perform.</em></>}
          lead="We build fast, SEO-stable, well-maintainable websites — on established systems like WordPress and TYPO3, or headless with Sanity. Not page-builder hostage situations, but content that is understood and guides users on purpose."
          breadcrumbs={[{ label: "Start", to: "/en" }, { label: "Services", to: "/en/services" }, { label: "Website Development" }]}
        />

        <Section number="02" kicker="Standpoint" headline={<>Websites as <em>functional systems</em> — not digital business cards.</>}>
          <RevealOnScroll>
            <div className="mb-10 rounded-lg border-l-4 border-lime bg-bg-subtle p-5 text-[15px] leading-relaxed text-ink-2">
              Need an online shop? That's a discipline of its own —{" "}
              <a href="/en/services/ecommerce-development" className="font-medium text-ink underline decoration-lime decoration-2 underline-offset-4 hover:decoration-ink">
                go to E-Commerce Development →
              </a>
            </div>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Many websites look good. But only a few fulfill their actual purpose. A professional website must be understood, build trust, and guide users on purpose. This is exactly where many projects fail. Not due to technology, but due to missing structure and clear goal orientation.</p>
              <p className="mb-5">In practice, we see the same problems repeatedly: content not aligned with user intent, page structures grown historically, load times and technical foundation slowing performance, marketing and tracking disconnected, systems hard to extend or maintain. The result: traffic is there — impact is missing.</p>
              <p>We develop websites as functional systems. At the start of every project, we don't begin with design but with understanding: what goals the website should fulfill, which users are being addressed, which content is truly relevant, which systems and interfaces are necessary. Only then does implementation begin.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Approach" headline={<>Four phases from <em>understanding</em> to a website that carries.</>}>
          <ProcessFlow steps={steps} />
        </Section>

        <Section number="04" kicker="Headless option" headline={<>Some projects don't need a new CMS. They need <em>a different foundation.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">For most websites, WordPress or TYPO3 are exactly right — if they're built as a performance asset and not allowed to suffocate in plug-in sprawl. But there are projects where a classic CMS hits its limits: multilingual sites, content that has to run on website, app and newsletter at the same time, or brands that demand maximum speed and full creative freedom. For exactly those cases, we use a headless approach with <strong>Sanity</strong>.</p>
              <p className="mb-8"><strong>What makes Sanity different:</strong> a single central content source feeds website, app and newsletter. Maintained once — delivered everywhere.</p>
            </div>

            <div className="mt-10 grid gap-8 md:grid-cols-2">
              <div className="rounded-lg border-2 border-lime bg-white p-7">
                <h3 className="mb-4 font-display text-xl text-ink">Headless with Sanity fits when …</h3>
                <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                  <li>the site is multilingual or delivered internationally</li>
                  <li>content runs across web, app and newsletter in parallel</li>
                  <li>an editorial team maintains it in a structured way</li>
                  <li>speed, SEO and long-term extensibility are central</li>
                </ul>
              </div>
              <div className="rounded-lg border border-rule bg-white p-7">
                <h3 className="mb-4 font-display text-xl text-ink">A classic CMS is better when …</h3>
                <ul className="space-y-3 text-[15px] leading-relaxed text-ink-2">
                  <li>it's a manageable corporate or brand site</li>
                  <li>the team wants to keep familiar WordPress/TYPO3 workflows</li>
                  <li>the upfront cost of a headless setup is disproportionate</li>
                </ul>
              </div>
            </div>

            <div className="mt-10 prose max-w-2xl text-[15px] leading-relaxed text-ink-3">
              <p className="mb-5"><strong className="text-ink">What you actually gain:</strong> very fast, statically built pages · clean multilingual without plug-in chaos · maintain once, use everywhere · tailored editing · low-maintenance and secure, since the content layer is hosted by Sanity.</p>
              <p><strong className="text-ink">What it costs:</strong> entry is cheap to free. Sanity works in three tiers — a generous <strong>Free</strong> tier (up to 20 users, ideal for starting out), the <strong>Growth</strong> tier from $15 per active seat per month, and <strong>Enterprise</strong> on request. We make the exact calculation transparent up front.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="05" kicker="Capabilities & stack" headline={<>What we use to make websites <em>resilient.</em></>}>
          <Capabilities items={capabilities} />
          <div className="mt-12">
            <p className="eyebrow mb-5">Stack</p>
            <StackGrid items={stack} />
          </div>
        </Section>

        <LogoReferences locale="en" number="06" />

        <RelatedServices
          locale="en"
          fromId="website-entwicklung"
          number="07"
          items={relatedBySource["website-entwicklung"]}
        />

        <Section number="08" kicker="Engagement">
          <CTABlock
            headline={<>A website that <em>carries</em> — not just presents</>}
            body="Whether classic CMS or headless with Sanity — we first clarify what makes economic sense. If you want a website that is understandable, capable, and future-proof, we'd be happy to discuss it."
            primary={{ label: "Get in touch", to: "/en/contact" }}
            secondary={{ label: "All services", to: "/en/services" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
